/**
 * useConnectApi – hooks for OptiBay Connect edge function calls and queries.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';

// ---------- Types ----------
export interface VisibilitySettings {
  user_id: string;
  visible_to_shops: boolean;
  share_notes_with_shops: boolean;
  open_to_work_status: 'seeking' | 'quietly_open' | 'not_seeking';
  location_zip: string | null;
  radius_miles: number;
  passport_roles: string[];
  updated_at: string;
}

export interface ConnectRequest {
  id: string;
  from_type: 'user' | 'shop';
  from_user_id: string | null;
  from_shop_id: string | null;
  to_type: 'user' | 'shop';
  to_user_id: string | null;
  to_shop_id: string | null;
  status: string;
  message_intro: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConnectThread {
  id: string;
  user_id: string;
  shop_id: string;
  status: string;
  created_at: string;
  user_last_read_at: string | null;
  shop_last_read_at: string | null;
}

export interface ConnectMessage {
  id: string;
  thread_id: string;
  sender_type: 'user' | 'shop';
  sender_user_id: string | null;
  body: string;
  created_at: string;
}

export interface TalentCard {
  user_id: string;
  display_name: string;
  roles: string[];
  open_to_work_status: string;
  distance_miles: number | null;
  technician_profile_id: string | null;
  technician_type_title?: string;
  technician_type_source?: string;
}

export interface PassportProfile {
  user_id: string;
  display_name: string | null;
  roles: string[];
  home_zip: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShopHiringData {
  recruiting_enabled: boolean;
  hiring_roles: string[];
  hiring_blurb: string | null;
}

// ---------- Visibility Settings (Passport) ----------
export function useVisibilitySettings() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['connect-visibility', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('passport_visibility_settings')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as VisibilitySettings | null;
    },
    enabled: !!user?.id,
  });
}

export function useUpdateVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<VisibilitySettings>) => {
      const { data, error } = await supabase.functions.invoke('update-passport-visibility', { body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['connect-visibility'] });
      toast({ title: 'Visibility updated' });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });
}

// ---------- Shop Hiring Settings ----------
export function useShopHiringSettings() {
  const { activeShop } = useAuth();
  return useQuery({
    queryKey: ['shop-hiring-settings', activeShop?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shops')
        .select('recruiting_enabled, hiring_roles, hiring_blurb')
        .eq('id', activeShop!.id)
        .single();
      if (error) throw error;
      return data as ShopHiringData;
    },
    enabled: !!activeShop?.id,
  });
}

export function useUpdateShopHiring() {
  const { activeShop } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { recruiting_enabled: boolean; hiring_roles: string[]; hiring_blurb: string | null }) => {
      const { data, error } = await supabase.functions.invoke('update-shop-recruiting', {
        body: { shopId: activeShop!.id, ...body },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shop-hiring-settings'] });
      toast({ title: 'Hiring settings updated' });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });
}

// ---------- Visible Talent (Shop side) ----------
export function useVisibleTalent(roleFilter?: string[]) {
  const { activeShop } = useAuth();
  return useQuery({
    queryKey: ['visible-talent', activeShop?.id, roleFilter],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_visible_talent', {
        p_shop_id: activeShop!.id,
        p_role_filter: roleFilter ?? null,
      });
      if (error) throw error;
      return (data || []) as TalentCard[];
    },
    enabled: !!activeShop?.id,
  });
}

// ---------- Connection Requests ----------
export function useMyConnectRequests() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['connect-requests', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('connect_requests')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as ConnectRequest[];
    },
    enabled: !!user?.id,
  });
}

export function useCreateConnectRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      from_type: 'user' | 'shop';
      from_shop_id?: string;
      to_type: 'user' | 'shop';
      to_user_id?: string;
      to_shop_id?: string;
      message_intro?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('create-connect-request', { body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['connect-requests'] });
      toast({ title: '🤝 Connection Request Sent', description: 'The recipient will be notified of your request.' });
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to send request', description: e.message, variant: 'destructive' });
    },
  });
}

export function useRespondConnectRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { request_id: string; action: 'accept' | 'decline' | 'cancel' }) => {
      const { data, error } = await supabase.functions.invoke('respond-connect-request', { body });
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['connect-requests'] });
      qc.invalidateQueries({ queryKey: ['connect-threads'] });
      if (vars.action === 'accept') {
        toast({ title: '✅ Connection Accepted', description: 'You can now message each other.' });
      } else if (vars.action === 'decline') {
        toast({ title: 'Connection Declined' });
      } else {
        toast({ title: 'Request cancelled' });
      }
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to update request', description: e.message, variant: 'destructive' });
    },
  });
}

// ---------- Threads + Messages ----------
export function useMyThreads() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['connect-threads', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('connect_threads')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as ConnectThread[];
    },
    enabled: !!user?.id,
  });
}

export function useThreadMessages(threadId: string | null) {
  return useQuery({
    queryKey: ['connect-messages', threadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('connect_messages')
        .select('*')
        .eq('thread_id', threadId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data || []) as ConnectMessage[];
    },
    enabled: !!threadId,
    refetchInterval: 10000,
  });
}

export function useSendConnectMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { thread_id: string; body: string }) => {
      const { data, error } = await supabase.functions.invoke('send-connect-message', { body });
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['connect-messages', vars.thread_id] });
      toast({ title: '💬 Message Sent' });
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to send message', description: e.message, variant: 'destructive' });
    },
  });
}

// ---------- Block / Report ----------
export function useBlockEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { blocked_user_id?: string; blocked_shop_id?: string; reason?: string }) => {
      const { data, error } = await supabase.functions.invoke('block-entity', { body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['connect-threads'] });
      qc.invalidateQueries({ queryKey: ['connect-requests'] });
      toast({ title: 'Blocked' });
    },
  });
}

export function useReportEntity() {
  return useMutation({
    mutationFn: async (body: { target_type: string; target_id: string; reason: string; details?: string }) => {
      const { data, error } = await supabase.functions.invoke('report-entity', { body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Report submitted' });
    },
  });
}

// ---------- Recruiting shops (passport-side directory) ----------
// Core-100 direction 15 (CORE-R-SEC-001): the server decides the audience. The
// directory is an RPC that returns only the published listing fields, only to a
// person who is discoverable on Connect; shop membership grants nothing here.
export interface RecruitingShop {
  id: string;
  name: string;
  hiring_roles: string[] | null;
  hiring_blurb: string | null;
}

export function useRecruitingShops() {
  return useQuery({
    queryKey: ['recruiting-shops'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('connect_recruiting_shops' as never);
      if (error) throw error;
      return ((data ?? []) as unknown) as RecruitingShop[];
    },
  });
}

/** Names of shops the caller has a Connect relationship with (request, thread, offer). */
export async function fetchCounterpartyShopNames(shopIds: string[]): Promise<Record<string, string>> {
  const unique = [...new Set(shopIds)];
  if (unique.length === 0) return {};
  const { data, error } = await supabase.rpc('connect_counterparty_shop_names' as never, { p_shop_ids: unique } as never);
  if (error || !data) return {};
  const map: Record<string, string> = {};
  for (const s of (data as unknown) as Array<{ id: string; name: string }>) map[s.id] = s.name;
  return map;
}

// ---------- Passport Profiles ----------
export function usePassportProfile() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['passport-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('passport_profiles')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as PassportProfile | null;
    },
    enabled: !!user?.id,
  });
}

export function useCreatePassportProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { display_name: string; roles: string[]; home_zip?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const trimmedName = body.display_name.trim();

      // 1. Upsert passport_profiles
      const { data: passportData, error: passportError } = await supabase
        .from('passport_profiles')
        .upsert({ user_id: user.id, ...body, display_name: trimmedName }, { onConflict: 'user_id' })
        .select()
        .single();
      if (passportError) throw passportError;

      // 2. Sync display name to auth metadata + profiles table
      await supabase.auth.updateUser({ data: { display_name: trimmedName } });
      await supabase
        .from('profiles')
        .update({ display_name: trimmedName })
        .eq('id', user.id);

      // 3. Create or update technician_profiles.
      // FIX-R37-HIGH-2: don't throw if the technician_profiles insert fails
      // — the passport_profiles row was the contract the caller asked for,
      // and rolling back is risky (passport_profiles upsert may have been
      // intended to merge with an existing row). Log the partial failure so
      // ops can repair the orphan; the user still has a valid passport.
      const { data: existingTechnicianProfile, error: technicianLookupError } = await supabase
        .from('technician_profiles')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (technicianLookupError) {
        console.warn('[useCreatePassportProfile] technician_profiles lookup failed (non-fatal):', technicianLookupError);
      } else if (!existingTechnicianProfile) {
        const { error: technicianInsertError } = await supabase
          .from('technician_profiles')
          .insert({
            user_id: user.id,
            full_name: trimmedName,
          });
        if (technicianInsertError) {
          console.warn(
            '[useCreatePassportProfile] technician_profiles insert failed; passport row created OK but bridge row missing:',
            technicianInsertError,
            { userId: user.id }
          );
        }
      }

      return passportData;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['passport-profile'] });
      toast({ title: 'Passport created!' });
    },
    onError: (e: Error) => {
      toast({ title: 'Error creating passport', description: e.message, variant: 'destructive' });
    },
  });
}

export function useUpdatePassportRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (roles: string[]) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('passport_profiles')
        .update({ roles })
        .eq('user_id', user.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['passport-profile'] });
    },
    onError: (e: Error) => {
      toast({ title: 'Error updating roles', description: e.message, variant: 'destructive' });
    },
  });
}

// ---------- Talent Profile (secured RPC) ----------
/**
 * Fetch a Passport-shaped talent profile.
 *
 * view_mode (R73 — A-C-02):
 *   - 'self'      → uses `get_talent_profile(null, userId)`     (owner-only)
 *   - 'connect'   → uses `get_talent_profile(shopId, userId)`   (DEFAULT, unchanged)
 *   - 'in_scope'  → uses `get_in_scope_passport(userId)`        (Floo hierarchy)
 */
export function useTalentProfile(
  shopId: string | null,
  userId: string,
  viewMode: 'self' | 'connect' | 'in_scope' = 'connect',
) {
  return useQuery({
    queryKey: ['talent-profile', viewMode, shopId, userId],
    queryFn: async () => {
      if (viewMode === 'in_scope') {
        const { data, error } = await supabase.rpc('get_in_scope_passport', {
          p_user_id: userId,
        });
        if (error) throw error;
        const results = data as TalentCard[];
        return results?.[0] ?? null;
      }

      const { data, error } = await supabase.rpc('get_talent_profile', {
        p_shop_id: shopId,
        p_user_id: userId,
      });
      if (error) throw error;
      const results = data as TalentCard[];
      return results?.[0] ?? null;
    },
    enabled: !!userId,
  });
}
